import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from 'components/CustomButtons/Button.js';
import { supabase } from 'common/src/config/supabaseClient';
import { apiUrl } from 'common';
import { FONT_FAMILY, WEB_MAIN_COLOR } from '../common/sharedFunctions';
import { colors } from '../components/Theme/WebTheme';

const useStyles = makeStyles((theme) => ({
  container: props => ({
    padding: theme.spacing(3),
    backgroundColor: colors.WHITE,
    borderRadius: 8,
    boxShadow: `0 2px 5px ${WEB_MAIN_COLOR(props?.settings?.appCat)}`,
  }),
  title: props => ({
    color: colors.WHITE,
    padding: theme.spacing(1.5),
    backgroundColor: WEB_MAIN_COLOR(props?.settings?.appCat),
    borderRadius: 8,
    fontFamily: FONT_FAMILY,
    fontSize: 18,
    marginBottom: theme.spacing(2),
  }),
  field: {
    width: '100%',
  },
  button: props => ({
    width: '100%',
    height: 42,
    backgroundColor: WEB_MAIN_COLOR(props?.settings?.appCat),
    color: colors.WHITE,
    fontFamily: FONT_FAMILY,
  }),
}));

const initialForm = {
  name: '',
  area_type: 'radius',
  city: '',
  country: '',
  center_lat: '',
  center_lng: '',
  radius_km: '',
  priority: 0,
};

export default function CompanyServiceAreas() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const auth = useSelector(state => state.auth);
  const classes = useStyles({ settings });
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [areas, setAreas] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadCompanies = async () => {
      if (!auth.profile?.uid) return;
      setError('');
      if (auth.profile.usertype === 'admin') {
        const { data, error: companyError } = await supabase
          .from('companies')
          .select('id, name, status')
          .order('name', { ascending: true });
        if (companyError) {
          setError(companyError.message);
          return;
        }
        setCompanies(data || []);
        if (!selectedCompanyId && data?.[0]) setSelectedCompanyId(data[0].id);
        return;
      }

      const { data: memberships, error: memberError } = await supabase
        .from('company_members')
        .select('company_id, companies(id, name, status)')
        .eq('user_id', auth.profile.uid)
        .eq('status', 'active');
      if (memberError) {
        setError(memberError.message);
        return;
      }
      const ownedCompanies = (memberships || [])
        .map(item => item.companies)
        .filter(Boolean);
      setCompanies(ownedCompanies);
      if (!selectedCompanyId && ownedCompanies[0]) setSelectedCompanyId(ownedCompanies[0].id);
    };

    loadCompanies();
  }, [auth.profile, selectedCompanyId]);

  const loadAreas = useCallback(async () => {
    if (!selectedCompanyId) {
      setAreas([]);
      return;
    }
    setLoading(true);
    setError('');
    const { data, error: areaError } = await supabase
      .from('company_service_areas')
      .select('*')
      .eq('company_id', selectedCompanyId)
      .order('priority', { ascending: false })
      .order('name', { ascending: true });
    setLoading(false);
    if (areaError) {
      setError(areaError.message);
      return;
    }
    setAreas(data || []);
  }, [selectedCompanyId]);

  const loadSubscription = useCallback(async () => {
    if (!selectedCompanyId) {
      setSubscription(null);
      return;
    }
    const { data, error: subscriptionError } = await supabase
      .from('company_subscriptions')
      .select('*')
      .eq('company_id', selectedCompanyId)
      .maybeSingle();
    if (subscriptionError) {
      setError(subscriptionError.message);
      return;
    }
    setSubscription(data);
  }, [selectedCompanyId]);

  useEffect(() => {
    loadAreas();
    loadSubscription();
  }, [loadAreas, loadSubscription]);

  const openBillingSession = async (path) => {
    if (!selectedCompanyId) return;
    setError('');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      setError('Sign in again before managing billing.');
      return;
    }
    const response = await fetch(apiUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        company_id: selectedCompanyId,
        origin: window.location.origin,
      }),
    });
    const result = await response.json();
    if (!response.ok) {
      setError(result.error || 'Unable to open Stripe billing.');
      return;
    }
    window.location.href = result.url;
  };

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const saveArea = async () => {
    if (!selectedCompanyId || !form.name) {
      setError(t('select_proper'));
      return;
    }
    const payload = {
      company_id: selectedCompanyId,
      name: form.name,
      area_type: form.area_type,
      city: form.area_type === 'city' ? form.city : null,
      country: form.country || null,
      center_lat: form.area_type === 'radius' && form.center_lat !== '' ? Number(form.center_lat) : null,
      center_lng: form.area_type === 'radius' && form.center_lng !== '' ? Number(form.center_lng) : null,
      radius_km: form.area_type === 'radius' && form.radius_km !== '' ? Number(form.radius_km) : null,
      priority: Number(form.priority || 0),
      active: true,
    };
    if (payload.area_type === 'radius' && (payload.center_lat === null || payload.center_lng === null || payload.radius_km === null)) {
      setError(t('select_proper'));
      return;
    }
    const { error: saveError } = await supabase
      .from('company_service_areas')
      .insert(payload);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setForm(initialForm);
    loadAreas();
  };

  const deleteArea = async (id) => {
    const { error: deleteError } = await supabase
      .from('company_service_areas')
      .delete()
      .eq('id', id);
    if (deleteError) {
      setError(deleteError.message);
      return;
    }
    loadAreas();
  };

  return (
    <div className={classes.container}>
      <Typography className={classes.title} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
        {t('service_areas')}
      </Typography>
      {error ? <Typography style={{ color: colors.RED, fontFamily: FONT_FAMILY, marginBottom: 12 }}>{error}</Typography> : null}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>{t('taxi_company')}</InputLabel>
            <Select
              value={selectedCompanyId}
              label={t('taxi_company')}
              onChange={(event) => setSelectedCompanyId(event.target.value)}
            >
              {companies.map(company => (
                <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField className={classes.field} name="name" label={t('name')} value={form.name} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>{t('type')}</InputLabel>
            <Select name="area_type" value={form.area_type} label={t('type')} onChange={handleChange}>
              <MenuItem value="radius">{t('radius')}</MenuItem>
              <MenuItem value="city">{t('city')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {form.area_type === 'city' ? (
          <Grid item xs={12} md={4}>
            <TextField className={classes.field} name="city" label={t('city')} value={form.city} onChange={handleChange} />
          </Grid>
        ) : (
          <>
            <Grid item xs={12} md={2}>
              <TextField className={classes.field} name="center_lat" label={t('latitude')} value={form.center_lat} onChange={handleChange} type="number" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField className={classes.field} name="center_lng" label={t('longitude')} value={form.center_lng} onChange={handleChange} type="number" />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField className={classes.field} name="radius_km" label={t('radius_km')} value={form.radius_km} onChange={handleChange} type="number" />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={2}>
          <TextField className={classes.field} name="country" label={t('country')} value={form.country} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField className={classes.field} name="priority" label={t('priority')} value={form.priority} onChange={handleChange} type="number" />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button className={classes.button} onClick={saveArea}>{t('add')}</Button>
        </Grid>
        <Grid item xs={12}>
          <Typography style={{ fontFamily: FONT_FAMILY }}>
            {t('billing_status')}: {subscription?.status || t('not_started')}
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <Button className={classes.button} onClick={() => openBillingSession('billing/checkout-session')}>
            {t('start_billing')}
          </Button>
        </Grid>
        <Grid item xs={12} md={3}>
          <Button className={classes.button} onClick={() => openBillingSession('billing/portal-session')}>
            {t('manage_billing')}
          </Button>
        </Grid>
      </Grid>

      <Table style={{ marginTop: 24 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('name')}</TableCell>
            <TableCell>{t('type')}</TableCell>
            <TableCell>{t('city')}</TableCell>
            <TableCell>{t('radius_km')}</TableCell>
            <TableCell>{t('priority')}</TableCell>
            <TableCell>{t('delete')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {areas.map(area => (
            <TableRow key={area.id}>
              <TableCell>{area.name}</TableCell>
              <TableCell>{t(area.area_type)}</TableCell>
              <TableCell>{area.city || '-'}</TableCell>
              <TableCell>{area.radius_km || '-'}</TableCell>
              <TableCell>{area.priority}</TableCell>
              <TableCell>
                <IconButton onClick={() => deleteArea(area.id)} size="small">
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && areas.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>{t('no_info_found')}</TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}
